import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { PlusIcon } from 'lucide-react'
import { Link, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useGetAvailableGamesQuery, useJoinGameMutation } from '../queries'
import { canJoinGame, formatGameStatus } from '../lib'
import { BuyInForm, buyInFormOpts } from '../form/buy-in-form'
import type { GetAvailableGamesRow } from '@/tygo/db'
import type { GameStatus } from '@/tygo/dto'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  USDCToString,
  dollarsToMicroUSDC,
  microUSDCToDollars,
} from '@/lib/currency'
import { Button } from '@/components/ui/button'
import { HttpError } from '@/lib/http'
import { cn } from '@/lib/utils'
import { useUserQuery } from '@/domains/user/queries'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAppForm } from '@/lib/form'
import { useBalance } from '@/domains/balance/hooks/useBalance'

const columnHelper = createColumnHelper<GetAvailableGamesRow>()

// NB: Using this as a stable reference to avoid re-rendering the table when the data is empty
const EMPTY_ARRAY: Array<GetAvailableGamesRow> = []

export function AvailableGamesTable() {
  const { data: user } = useUserQuery()
  const { data: availableGames } = useGetAvailableGamesQuery()
  const joinGameMutation = useJoinGameMutation()
  const navigate = useNavigate()
  const balance = useBalance()

  const [selectedGame, setSelectedGame] = useState<GetAvailableGamesRow | null>(
    null,
  )
  const [buyInDialogOpen, setBuyInDialogOpen] = useState(false)

  const form = useAppForm({
    ...buyInFormOpts,
    onSubmit: async ({ value }) => {
      if (!selectedGame) return

      const buyInMicro =
        selectedGame.min_buy_in === selectedGame.max_buy_in
          ? selectedGame.min_buy_in
          : dollarsToMicroUSDC(parseFloat(value.buyInAmount))

      try {
        const gamePlayer = await joinGameMutation.mutateAsync({
          gameId: selectedGame.id,
          buyInAmount: buyInMicro,
        })
        setBuyInDialogOpen(false)
        form.reset()
        navigate({ to: '/game/$id', params: { id: gamePlayer.game_id } })
      } catch (error) {
        toast.error(
          error instanceof HttpError ? error.message : 'Failed to join game',
        )
      }
    },
  })

  function handleGameClick(game: GetAvailableGamesRow) {
    if (!user || !canJoinGame(game, user)) return
    setSelectedGame(game)
    form.setFieldValue(
      'buyInAmount',
      microUSDCToDollars(game.min_buy_in).toFixed(2),
    )
    setBuyInDialogOpen(true)
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ row }) => {
          return (
            <div>{formatGameStatus(row.original.status as GameStatus)}</div>
          )
        },
      }),
      columnHelper.display({
        header: 'Blinds',
        cell: ({ row }) => {
          return (
            <div>
              ${USDCToString(row.original.small_blind)} / $
              {USDCToString(row.original.big_blind)}
            </div>
          )
        },
      }),
      columnHelper.display({
        header: 'Players',
        cell: ({ row }) => {
          return (
            <div>
              {row.original.player_count} / {row.original.player_limit}
            </div>
          )
        },
      }),
      columnHelper.accessor('auto_start_count', {
        header: 'Auto Start',
        cell: ({ row }) => {
          return (
            <div>
              {row.original.status === 'waiting'
                ? row.original.auto_start_count
                : '-'}
            </div>
          )
        },
      }),
    ],
    [],
  )

  const table = useReactTable({
    data: availableGames ?? EMPTY_ARRAY,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="mt-10">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-medium">Available Games</h1>
          <p className="text-sm text-muted-foreground">
            Click on a game to join.
          </p>
        </div>
        <Link to="/game/new">
          <Button>
            <PlusIcon className="size-4" />
            <span className="hidden md:block">Create</span>
          </Button>
        </Link>
      </div>
      <Table className="mt-4">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="cursor-default">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <Tooltip key={row.id}>
                <TooltipTrigger asChild>
                  <TableRow
                    onClick={() => handleGameClick(row.original)}
                    className={cn(
                      joinGameMutation.isPending &&
                        'opacity-50 pointer-events-none',
                      (!user || !canJoinGame(row.original, user)) &&
                        'opacity-60 cursor-not-allowed',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TooltipTrigger>
                <TooltipContent
                  className={cn(
                    user && canJoinGame(row.original, user) && 'hidden',
                  )}
                >
                  {user
                    ? 'You do not have enough funds to cover the minimum buy-in.'
                    : 'You need to be logged in to join a game.'}
                </TooltipContent>
              </Tooltip>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No games available.{' '}
                <Link to="/game/new" className="underline">
                  Create a game
                </Link>{' '}
                to get started.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <BuyInForm
        form={form}
        game={selectedGame}
        balance={balance ?? 0}
        open={buyInDialogOpen}
        onOpenChange={(open) => {
          setBuyInDialogOpen(open)
          if (!open) form.reset()
        }}
      />
    </div>
  )
}
